import LoadingScreen from '../components/LoadingScreen';
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAppContext } from '../context/AppContext';
import * as XLSX from 'xlsx';

export default function SuperAdminSettingsScreen() {
  const { user, refreshAppSettings } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState({});
  const [savingPrice, setSavingPrice] = useState(null);
  const [todayEnabled, setTodayEnabled] = useState(true);
  const [togglingToday, setTogglingToday] = useState(false);
  const [minOrderQty, setMinOrderQty] = useState('2');
  const [savingMinQty, setSavingMinQty] = useState(false);
  const [webCatalogOffset, setWebCatalogOffset] = useState('280');
  const [savingWebOffset, setSavingWebOffset] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [adminSearch, setAdminSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const today = new Date();
  const [exportFrom, setExportFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]);
  const [exportTo, setExportTo] = useState(today.toISOString().split('T')[0]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prods, settings, ann, adminList] = await Promise.all([api.products.list(), api.settings.get(), api.announcements.list(), api.profile.admins()]);
      if (prods) { setProducts(prods); const p = {}; prods.forEach(item => { p[item.id] = String(item.price); }); setPrices(p); }
      if (settings) {
        setTodayEnabled(settings.today_delivery_enabled);
        setMinOrderQty(String(settings.min_order_quantity ?? 2));
        setWebCatalogOffset(String(settings.web_catalog_price_offset ?? 280));
      }
      if (ann) setAnnouncements(ann);
      if (adminList) setAdmins(adminList);
    } catch { alert('Не удалось загрузить настройки'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePrice = async (product) => {
    const val = parseInt(prices[product.id], 10);
    if (isNaN(val) || val <= 0) { alert('Введите корректную цену'); return; }
    if (val === product.price) return;
    setSavingPrice(product.id);
    try {
      const updated = await api.products.updatePrice(product.id, val);
      setProducts(prev => prev.map(p => p.id === product.id ? updated : p));
      alert(`${product.brand}: ${val} ₽ — сохранено`);
    } catch (err) { alert(err?.message || 'Не удалось сохранить цену'); }
    finally { setSavingPrice(null); }
  };

  const toggleToday = async (value) => {
    setTogglingToday(true);
    try { await api.settings.update({ today_delivery_enabled: value }); setTodayEnabled(value); }
    catch { alert('Не удалось изменить настройку'); }
    finally { setTogglingToday(false); }
  };

  const saveMinQty = async () => {
    const val = parseInt(minOrderQty, 10);
    if (isNaN(val) || val < 1) { alert('Минимум 1 баллон'); return; }
    setSavingMinQty(true);
    try {
      await api.settings.update({ min_order_quantity: val });
      await refreshAppSettings?.();
      alert(`Минимальное количество: ${val} шт. — сохранено`);
    }
    catch { alert('Не удалось сохранить'); }
    finally { setSavingMinQty(false); }
  };

  const saveWebCatalogOffset = async () => {
    const val = parseInt(webCatalogOffset, 10);
    if (isNaN(val) || val < 0) { alert('Введите число от 0'); return; }
    setSavingWebOffset(true);
    try {
      await api.settings.update({ web_catalog_price_offset: val });
      await refreshAppSettings?.();
      alert(`Для веб-каталога из цены вычитается ${val} ₽ — сохранено`);
    }
    catch { alert('Не удалось сохранить'); }
    finally { setSavingWebOffset(false); }
  };

  const publishAnnouncement = async () => {
    if (!newMsg.trim()) return;
    setPublishing(true);
    try { const data = await api.announcements.create(newMsg.trim()); setAnnouncements(prev => [data, ...prev]); setNewMsg(''); }
    catch (err) { alert(err?.message || 'Не удалось опубликовать'); }
    finally { setPublishing(false); }
  };

  const toggleAnnouncement = async (ann) => {
    try { const updated = await api.announcements.toggle(ann.id); setAnnouncements(prev => prev.map(a => a.id === ann.id ? updated : a)); }
    catch { alert('Не удалось изменить статус'); }
  };

  const deleteAnnouncement = async (ann) => {
    if (!window.confirm('Удалить объявление?')) return;
    try { await api.announcements.delete(ann.id); setAnnouncements(prev => prev.filter(a => a.id !== ann.id)); }
    catch {}
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const data = await api.orders.export(exportFrom, exportTo);
      if (!data || data.length === 0) { alert('За выбранный период заказов не найдено'); return; }
      const rows = data.map(o => ({
        '№': o.id,
        'Дата закрытия': o.closed_at ? new Date(o.closed_at).toLocaleDateString('ru-RU') : '—',
        'Вода': o.brand ? `${o.brand} ${o.volume || ''}`.trim() : '—',
        'Телефон': o.phone || '—', 'Город': o.city || '—',
        'Район': o.district || '—', 'Адрес': o.address || '—', 'Бутыли': o.quantity,
        'Обмен': o.exchange_count || 0, 'Сумма': o.total_price || '—',
        'Способ оплаты': o.payment_method || '—', 'Статус': o.status || '—',
        'Кто создал': o.created_by_admin_name || 'Клиент', 'Кто закрыл': o.closed_by_name || '—',
        'Комментарий': o.comment || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Заказы');
      XLSX.writeFile(wb, `заказы_${exportFrom}_${exportTo}.xlsx`);
    } catch (e) { alert(e?.message || 'Не удалось создать файл'); }
    finally { setExporting(false); }
  };

  const searchUsers = async () => {
    const q = adminSearch.trim();
    if (!q) return;
    setSearching(true); setSearchResults([]);
    try {
      const data = await api.profile.users();
      setSearchResults((data || []).filter(u => !u.is_superadmin && !u.is_admin && (u.phone?.includes(q) || u.name?.toLowerCase().includes(q.toLowerCase()))).slice(0, 5));
    } catch { alert('Не удалось найти пользователей'); }
    finally { setSearching(false); }
  };

  const grantAdmin = async (profile) => {
    try { await api.profile.toggleAdmin(profile.id); setAdmins(prev => [...prev, profile]); setSearchResults(prev => prev.filter(u => u.id !== profile.id)); setAdminSearch(''); }
    catch (e) { alert(e.detail || 'Не удалось выдать права'); }
  };

  const revokeAdmin = async (profile) => {
    if (!window.confirm(`Снять роль администратора у ${profile.name || profile.phone}?`)) return;
    try { await api.profile.toggleAdmin(profile.id); setAdmins(prev => prev.filter(a => a.id !== profile.id)); }
    catch (e) { alert(e.detail || 'Не удалось снять права'); }
  };

  if (!user?.isSuperAdmin) return <div style={centered}>Доступ только для суперадмина</div>;
  if (loading) return <LoadingScreen />;

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--primary)', marginTop: 16, marginBottom: 20 }}>Управление</h1>

      {/* Цены */}
      <div style={section}>
        <h3 style={sectionTitle}>💧 Цены на воду</h3>
        {products.map(item => (
          <div key={item.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 24 }}>{item.emoji || '💧'}</span>
              <div><div style={{ fontWeight: 700, fontSize: 14 }}>{item.brand}</div><div style={{ fontSize: 12, color: 'var(--text-light)' }}>{item.volume}</div></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="number" value={prices[item.id] ?? ''} onChange={e => setPrices(prev => ({ ...prev, [item.id]: e.target.value.replace(/[^\d]/g, '') }))}
                style={{ width: 80, textAlign: 'center', fontWeight: 700, padding: '8px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 15, outline: 'none' }} />
              <span style={{ color: 'var(--text-light)', fontWeight: 600 }}>₽</span>
              <button onClick={() => savePrice(item)} disabled={savingPrice !== null} style={{ flex: 1, padding: '9px', borderRadius: 10, background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: savingPrice === item.id ? 0.5 : 1 }}>
                {savingPrice === item.id ? '...' : 'Сохранить'}
              </button>
            </div>
          </div>
        ))}
        <p style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 12, lineHeight: 1.45 }}>
          Цена в веб-каталоге для клиентов: <strong>цена выше − смещение</strong> (ниже). Тариф в заказах и у курьера — по этой каталожной цене.
        </p>
      </div>

      {/* Веб-каталог: смещение цены */}
      <div style={section}>
        <h3 style={sectionTitle}>🌐 Веб-каталог</h3>
        <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 10 }}>Сколько ₽ вычитать из цены товара при показе в каталоге и при оформлении заказа в браузере (по умолчанию 280).</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="number" min={0} value={webCatalogOffset} onChange={e => setWebCatalogOffset(e.target.value.replace(/[^\d]/g, ''))}
            style={{ width: 100, textAlign: 'center', fontWeight: 700, padding: '8px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 15, outline: 'none' }} />
          <span style={{ color: 'var(--text-light)', fontWeight: 600 }}>₽</span>
          <button onClick={saveWebCatalogOffset} disabled={savingWebOffset} style={{ flex: 1, padding: '9px', borderRadius: 10, background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: savingWebOffset ? 0.5 : 1 }}>
            {savingWebOffset ? '...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {/* Доставка сегодня */}
      <div style={section}>
        <h3 style={sectionTitle}>🚚 Доставка «Сегодня»</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{todayEnabled ? 'Включена' : 'Отключена'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-light)', lineHeight: 1.4 }}>{todayEnabled ? 'Клиенты могут заказывать на сегодня до 18:00 по Москве (после — кнопка неактивна)' : 'Кнопка «Сегодня» скрыта'}</div>
          </div>
          <button onClick={() => toggleToday(!todayEnabled)} disabled={togglingToday}
            style={{ padding: '8px 20px', borderRadius: 20, background: todayEnabled ? 'var(--primary)' : 'var(--input-bg)', color: todayEnabled ? '#fff' : 'var(--text)', fontWeight: 700, fontSize: 14, border: '1.5px solid var(--border)', cursor: 'pointer' }}>
            {todayEnabled ? 'Вкл' : 'Выкл'}
          </button>
        </div>
      </div>

      {/* Мин. количество */}
      <div style={section}>
        <h3 style={sectionTitle}>🔢 Минимальный заказ</h3>
        <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 10 }}>Минимальное количество баллонов для оформления заказа</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="number" min={1} value={minOrderQty} onChange={e => setMinOrderQty(e.target.value.replace(/[^\d]/g, ''))}
            style={{ width: 80, textAlign: 'center', fontWeight: 700, padding: '8px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 15, outline: 'none' }} />
          <span style={{ color: 'var(--text-light)', fontWeight: 600 }}>шт.</span>
          <button onClick={saveMinQty} disabled={savingMinQty} style={{ flex: 1, padding: '9px', borderRadius: 10, background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: savingMinQty ? 0.5 : 1 }}>
            {savingMinQty ? '...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {/* Объявления */}
      <div style={section}>
        <h3 style={sectionTitle}>📢 Объявления</h3>
        <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 10 }}>Активное объявление отображается баннером на главном экране</p>
        <textarea placeholder="Текст объявления..." value={newMsg} onChange={e => setNewMsg(e.target.value)} style={{ display: 'block', width: '100%', minHeight: 80, background: 'var(--input-bg)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '10px 14px', fontSize: 14, color: 'var(--text)', outline: 'none', resize: 'vertical', marginBottom: 10 }} />
        <button onClick={publishAnnouncement} disabled={!newMsg.trim() || publishing} style={{ width: '100%', padding: 12, borderRadius: 12, background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: (!newMsg.trim() || publishing) ? 0.45 : 1 }}>
          {publishing ? '...' : 'Опубликовать'}
        </button>
        {announcements.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Список объявлений</p>
            {announcements.map(ann => (
              <div key={ann.id} style={{ borderRadius: 12, border: `1.5px solid ${ann.active ? 'var(--primary)' : 'var(--border)'}`, padding: 12, marginBottom: 8, background: 'var(--input-bg)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: ann.active ? 'var(--primary)' : 'var(--border)', color: ann.active ? '#fff' : 'var(--text)' }}>{ann.active ? 'Активно' : 'Скрыто'}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-light)' }}>{new Date(ann.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 8 }}>{ann.message}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => toggleAnnouncement(ann)} style={{ flex: 1, padding: '6px', borderRadius: 8, border: '1px solid var(--primary)', background: 'none', color: 'var(--primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{ann.active ? 'Скрыть' : 'Показать'}</button>
                  <button onClick={() => deleteAnnouncement(ann)} style={{ flex: 1, padding: '6px', borderRadius: 8, border: '1px solid #FF3B30', background: 'none', color: '#FF3B30', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Удалить</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Экспорт */}
      <div style={section}>
        <h3 style={sectionTitle}>📊 Выгрузка заказов</h3>
        <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 12 }}>Экспорт заказов за период в Excel</p>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}><p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 4, fontWeight: 600 }}>С</p><input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 14, outline: 'none' }} /></div>
          <div style={{ flex: 1 }}><p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 4, fontWeight: 600 }}>По</p><input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 14, outline: 'none' }} /></div>
        </div>
        <button onClick={exportToExcel} disabled={exporting} style={{ width: '100%', padding: 12, borderRadius: 12, background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: exporting ? 0.5 : 1 }}>
          {exporting ? '...' : '📥 Скачать Excel'}
        </button>
      </div>

      {/* Администраторы */}
      <div style={section}>
        <h3 style={sectionTitle}>👷 Администраторы</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input placeholder="Телефон или имя пользователя..." value={adminSearch} onChange={e => { setAdminSearch(e.target.value); setSearchResults([]); }} onKeyDown={e => e.key === 'Enter' && searchUsers()}
            style={{ flex: 1, padding: '9px 12px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 14, outline: 'none' }} />
          <button onClick={searchUsers} disabled={!adminSearch.trim() || searching} style={{ padding: '9px 16px', borderRadius: 10, background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: (!adminSearch.trim() || searching) ? 0.5 : 1 }}>
            {searching ? '...' : 'Найти'}
          </button>
        </div>
        {searchResults.map(u => (
          <div key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--border)', gap: 12 }}>
            <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 14 }}>{u.name || '—'}</div><div style={{ fontSize: 12, color: 'var(--text-light)' }}>{u.phone}</div></div>
            <button onClick={() => grantAdmin(u)} style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>+ Назначить</button>
          </div>
        ))}
        {searchResults.length === 0 && adminSearch.trim() && !searching && <p style={{ fontSize: 13, color: 'var(--text-light)', textAlign: 'center', padding: '8px 0' }}>Пользователь не найден</p>}
        {admins.length > 0 && (
          <>
            <p style={{ fontSize: 14, fontWeight: 600, marginTop: 12, marginBottom: 8 }}>Текущие администраторы</p>
            {admins.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--border)', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(26,109,181,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--primary)', fontSize: 16 }}>{(a.name?.[0] || '?').toUpperCase()}</div>
                <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 14 }}>{a.name || '—'}</div><div style={{ fontSize: 12, color: 'var(--text-light)' }}>{a.phone}</div></div>
                <button onClick={() => revokeAdmin(a)} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #FF3B30', background: 'none', color: '#FF3B30', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Снять</button>
              </div>
            ))}
          </>
        )}
        {admins.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-light)', textAlign: 'center', padding: '8px 0' }}>Нет назначенных администраторов</p>}
      </div>
    </div>
  );
}

const centered = { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', fontSize: 16, color: 'var(--text-light)' };
const section = { background: 'var(--card)', borderRadius: 16, padding: 16, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' };
const sectionTitle = { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 12 };
